'use client';

import React, { useState, useMemo } from 'react';
import { useBinanceStream } from './useBinanceStream';
import { colors, baseStyles } from './SharedUI';

const TICK_SIZES = [0.0001, 0.001, 0.01, 0.1, 1, 10, 100, 1000];

export default function DiffOrderBook({ symbol = 'BTCUSDT' }) {
  const [tickSize, setTickSize] = useState(1);
  const { streamData, wsStatus, diffPulse } = useBinanceStream(symbol, true);

  const activeColors = useMemo(() => ({
    bg: colors?.bg || '#0b0e14',
    border: colors?.border || '#2b3139',
    green: colors?.green || '#089981',
    red: colors?.red || '#f23645',
    textSub: colors?.textSub || '#848e9c',
    textMain: colors?.textMain || '#ffffff'
  }), []);

  const formatVolume = (val) => {
    if (val >= 1000) return val.toFixed(2);
    if (val >= 1) return val.toFixed(4);
    return val.toFixed(6);
  };

  const processedData = useMemo(() => {
    // 🛠️ VÁ LỖI CHỐNG CRASH 1: Kiểm tra an toàn streamData bằng toán tử ?. để không bao giờ bị sập nếu dính undefined
    const rawBids = streamData?.bidsDiff || {};
    const rawAsks = streamData?.asksDiff || {};

    const aggregateAndSort = (rawLevels, isBid) => {
      // Phòng vệ bổ sung nếu dữ liệu truyền vào không phải Object
      if (!rawLevels || typeof rawLevels !== 'object') return [];
      
      const grouped = {};

      Object.entries(rawLevels).forEach(([priceStr, vol]) => {
        const price = parseFloat(priceStr);
        const roundedPrice = isBid
          ? Math.floor(price / tickSize) * tickSize
          : Math.ceil(price / tickSize) * tickSize;

        const key = roundedPrice.toFixed(4);
        grouped[key] = (grouped[key] || 0) + vol;
      });

      const sortedPrices = Object.keys(grouped)
        .map(p => parseFloat(p))
        .sort((a, b) => isBid ? b - a : a - b);

      let cumulativeSum = 0;
      return sortedPrices.map(price => {
        const volume = grouped[price.toFixed(4)];
        cumulativeSum += volume;
        return { price, volume, cumulative: cumulativeSum };
      });
    };

    let bids = aggregateAndSort(rawBids, true);
    let asks = aggregateAndSort(rawAsks, false);

    bids = bids.slice(0, 10);
    asks = asks.slice(0, 10);

    // 🛠️ VÁ LỖI CHỐNG CRASH 2: Chốt chặn an toàn tuyệt đối. 
    // Kiểm tra độ dài mảng > 0 trước rồi mới đọc dữ liệu, triệt tiêu hoàn toàn lỗi đọc thuộc tính của undefined
    const lastBidCumulative = bids.length > 0 && bids[bids.length - 1] ? bids[bids.length - 1].cumulative : 1;
    const lastAskCumulative = asks.length > 0 && asks[asks.length - 1] ? asks[asks.length - 1].cumulative : 1;

    const maxCumulative = Math.max(lastBidCumulative || 1, lastAskCumulative || 1);

    return { bids, asks, maxCumulative };
  }, [diffPulse, tickSize, streamData?.bidsDiff, streamData?.asksDiff]);

  // 🛠️ VÁ LỖI CHỐNG CRASH 3: Bảo vệ an toàn cho biến currentPrice
  const currentPrice = streamData?.price || processedData.asks[0]?.price || processedData.bids[0]?.price || '---';

  // Chốt chặn giao diện: Nếu đang tải snapshot hoặc đang trong tiến trình đổi sang coin khác, giữ màn hình chờ an toàn
  if (!streamData?.bidsDiff || Object.keys(streamData.bidsDiff).length === 0) {
    return (
      <div style={{ backgroundColor: activeColors.bg, color: wsStatus?.color || '#fbbf24', border: `1px solid ${activeColors.border}`, maxWidth: '600px', margin: '0 auto', padding: '20px', borderRadius: '8px', fontFamily: 'monospace', fontSize: '13px', fontWeight: 'bold' }}>
        ⏳ {wsStatus?.text || 'ĐANG KẾT NỐI'}... Đang đồng bộ luồng dữ liệu cặp {symbol}...
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: activeColors.bg, color: activeColors.textMain, padding: '20px', borderRadius: '8px', border: `1px solid ${activeColors.border}`, maxWidth: '600px', margin: '0 auto', fontFamily: 'monospace', boxSizing: 'border-box' }}>
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <div style={{ fontWeight: 'bold', fontSize: '14px', color: activeColors.textMain }}>ORDER BOOK (SHARED DIFF)</div>
          <div style={{ fontSize: '10px', color: wsStatus?.color || '#22c55e', fontWeight: 'bold', marginTop: '2px' }}>
            {wsStatus?.text || 'LIVE'} (250MS PULSE)
          </div>
        </div>
        
        <select 
          value={tickSize} 
          onChange={(e) => setTickSize(parseFloat(e.target.value))}
          style={{ ...baseStyles?.select, background: 'rgba(255,255,255,0.05)', color: activeColors.textMain, border: `1px solid ${activeColors.border}`, padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}
        >
          {TICK_SIZES.map(size => (
            <option key={size} value={size} style={{ background: activeColors.bg }}>
              {size >= 1 ? size : size.toFixed(4)}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', fontSize: '11px', color: activeColors.textSub, marginBottom: '8px', padding: '0 4px', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        <div>Giá ({symbol.replace('USDT','')})</div>
        <div style={{ textAlign: 'right' }}>K.Lượng (Size)</div>
        <div style={{ textAlign: 'right' }}>Tích luỹ (Depth)</div>
      </div>

      {/* Phe bán (Asks) */}
      <div style={{ display: 'flex', flexDirection: 'column-reverse', minHeight: '240px', maxHeight: '680px' }}>
        {processedData.asks.map((ask, idx) => {
          const widthPct = (ask.cumulative / processedData.maxCumulative) * 100;
          return (
            <div key={`ask-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', fontSize: '13px', padding: '3px 4px', position: 'relative', zIndex: 1, alignItems: 'center' }}>
              <div style={{ color: activeColors.red, fontWeight: 'bold' }}>{ask.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div style={{ textAlign: 'right', color: activeColors.textMain }}>{formatVolume(ask.volume)}</div>
              <div style={{ textAlign: 'right', color: activeColors.textSub }}>{formatVolume(ask.cumulative)}</div>
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: `${widthPct}%`, backgroundColor: `${activeColors.red}18`, zIndex: -1, transition: 'width 0.1s ease' }} />
            </div>
          );
        })}
      </div>

      {/* Giá trung tâm */}
      <div style={{ textAlign: 'center', margin: '12px 0', padding: '8px 0', borderTop: `1px dashed ${activeColors.border}`, borderBottom: `1px dashed ${activeColors.border}`, fontSize: '18px', fontWeight: 'black', color: activeColors.green, backgroundColor: 'rgba(0,0,0,0.2)' }}>
        {typeof currentPrice === 'number' ? currentPrice.toLocaleString(undefined, { minimumFractionDigits: 2 }) : currentPrice}
      </div>

      {/* Phe mua (Bids) */}
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '240px', maxHeight: '680px' }}>
        {processedData.bids.map((bid, idx) => {
          const widthPct = (bid.cumulative / processedData.maxCumulative) * 100;
          return (
            <div key={`bid-${idx}`} style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr', fontSize: '13px', padding: '3px 4px', position: 'relative', zIndex: 1, alignItems: 'center' }}>
              <div style={{ color: activeColors.green, fontWeight: 'bold' }}>{bid.price.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
              <div style={{ textAlign: 'right', color: activeColors.textMain }}>{formatVolume(bid.volume)}</div>
              <div style={{ textAlign: 'right', color: activeColors.textSub }}>{formatVolume(bid.cumulative)}</div>
              <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: `${widthPct}%`, backgroundColor: `${activeColors.green}18`, zIndex: -1, transition: 'width 0.1s ease' }} />
            </div>
          );
        })}
      </div>

    </div>
  );
}
